// The standard library — written in STM itself.
//
// The declarations themselves live in freestanding source files under `stdlib/`
// (split by topic — `core.stm`, `iter.stm`, `collections.stm`, `strings.stm`,
// `io.stm`, `net.stm`, `bufio.stm`, `json.stm`, `logging.stm` — all pooled into the one
// prelude), loaded here as `STDLIB_FILES` and parsed by the real parser — one
// file at a time, so each declaration remembers which file it came from (for
// go-to-definition) — so the compiler reads its own prelude the same way it reads
// a user program. Two kinds of thing are declared there:
//
//   * **Pure declarations** — the built-in structs (`Pair`), enums (`Option`,
//     `Res`), and traits (`Show`, the operator traits, `Iterator`,
//     `Iterable`). These have no native part; they are prepended to every program
//     (a program may shadow one by declaring the same name).
//
//   * **`builtin fn` declarations** — the built-in *functions* (`print`, `str`,
//     `sqrt`, …). The `builtin` keyword says "declared here, implemented
//     natively": each backend plugs an implementation in *by name* (the
//     interpreter's `builtins` table / `evalCall`; the Go backend's
//     `emitBuiltin`), and the checker types a call from the declaration. This is
//     the rule — everything is declared in STM source; native implementations are
//     plugged into the `builtin` declarations.
//
// This module parses the stdlib sources and exposes the declarations, plus the
// derived `BUILTINS` table. The built-in generic containers (`Set`/`Dict`/`List`/
// `Future`) are natively-backed collections, but the checker types them as
// ordinary generic types: their `impl` blocks in `collections.stm` (a block of
// `builtin fn`s, mirroring `str`) are the method source, resolved through the same
// impl + substitution path a generic struct's methods use. `containerTypeParams`
// supplies a container's declared arity, the analogue of a struct's `typeParams`.

import type { EnumDecl, FnDecl, ImplDecl, StructDecl, TraitDecl, TypeExpr } from "./ast.js";
import { isGenericContainer } from "./ast.js";
import { parse } from "./parser.js";
import { STDLIB_FILES, STDLIB_SRC } from "./stdlib-src.js";
import { Span } from "./token.js";
import { BOOL, FLOAT, INT, STRING, UNKNOWN, VOID } from "./types.js";
import type { Type } from "./types.js";

/** The standard library's source files (from `stdlib/*.stm`). Parsed once into
 *  the prelude every program gets (see `stdlibDecls`). Re-exported for tooling
 *  (`STDLIB_SRC` is the concatenation; `STDLIB_FILES` keeps per-file identity). */
export { STDLIB_FILES, STDLIB_SRC };

// ---------------------------------------------------------------------------
// Parsing the source into the prelude declarations.
// ---------------------------------------------------------------------------

/** Recursively stamp every source span in a parsed prelude node with the file it
 *  came from. Each stdlib file is parsed on its own, so its spans already carry
 *  real per-file offsets/lines — we keep those verbatim (so a debugger, a
 *  go-to-definition target, or a cross-file find-usages can point at the exact
 *  line inside a stdlib body) and only add the `source` tag naming the file.
 *
 *  That tag is the discriminator the rest of the compiler relies on: a span with
 *  no `source` indexes the user's document, a `source`-tagged span indexes the
 *  named bundled file. Editor queries over the user's buffer, and the CLI
 *  diagnostics renderer, filter on `source` so a prelude span is never read as a
 *  position in the user's text (see `LanguageService.diagnostics` and
 *  `checkSource`). */
function tagPreludeSource<T>(node: T, source: string): T {
  if (Array.isArray(node)) {
    for (const item of node) tagPreludeSource(item, source);
  } else if (node && typeof node === "object") {
    const obj = node as Record<string, unknown>;
    for (const key of Object.keys(obj)) {
      if (key === "span" || key.endsWith("Span")) {
        const span = obj[key] as Span | undefined;
        if (span && typeof span === "object" && "start" in span) {
          obj[key] = { start: span.start, end: span.end, source };
        }
      } else {
        tagPreludeSource(obj[key], source);
      }
    }
  }
  return node;
}

interface StdlibDecls {
  fns: FnDecl[];
  structs: StructDecl[];
  enums: EnumDecl[];
  traits: TraitDecl[];
  impls: ImplDecl[];
}

let cached: StdlibDecls | null = null;

/** Parse every stdlib file once and partition its declarations by kind. Each
 *  file is parsed on its own and its spans tagged with that file's id, so a
 *  declaration remembers which file it came from (for go-to-definition). */
function stdlibDecls(): StdlibDecls {
  if (cached) return cached;
  const result: StdlibDecls = { fns: [], structs: [], enums: [], traits: [], impls: [] };
  for (const file of STDLIB_FILES) {
    const program = tagPreludeSource(parse(file.src), file.id);
    for (const d of program.decls) {
      if (d.kind === "fn") result.fns.push(d);
      else if (d.kind === "struct") result.structs.push(d);
      else if (d.kind === "enum") result.enums.push(d);
      else if (d.kind === "trait") result.traits.push(d);
      else if (d.kind === "impl") result.impls.push(d);
    }
  }
  return (cached = result);
}

/** The built-in structs every program gets: `Pair`, the iterator cursors, and the
 *  `builtin struct` container declarations (`Set`/`Dict`/`List`/`Future`). */
export function builtinStructs(): StructDecl[] {
  return stdlibDecls().structs;
}

/** The built-in enums every program gets (`Option`, `Res`). */
export function builtinEnums(): EnumDecl[] {
  return stdlibDecls().enums;
}

/** The built-in traits every program gets. A program may shadow any of them by
 *  declaring its own trait of the same name. */
export function builtinTraits(): TraitDecl[] {
  return stdlibDecls().traits;
}

/** The built-in `impl` blocks the prelude *processes* — e.g. the `Iterable`/
 *  `Iterator` impls for the iterator adapters (`MapIterable`) behind `Iterable.map`.
 *  Every backend that resolves methods (checker, interpreter, monomorphize) must
 *  include these, just like the built-in structs and traits.
 *
 *  The *inherent* container impls (the flat methods — `add`/`get`/`size`/…) are
 *  *excluded* here because this list is shared with the backends: the monomorphizer
 *  and interpreter lower a container's built-in methods to native primitives
 *  (`builtinMethod` / `setMethod`), so processing the inherent impls as ordinary
 *  methods would lower `set.add` to a non-existent `Set_add` static call. The
 *  checker, which only *types* them, pulls them in separately (see
 *  `builtinContainerInherentImpls`) to resolve them like any generic type's methods.
 *
 *  The container *`Iterable`* impls, by contrast, are kept: processing them is what
 *  makes a set/dict a genuine `Iterable` (with a real `iter()` -> `SetIter`/
 *  `DictIter` cursor), so the trait's `map`/`filter`/`reduce` defaults work on it. */
export function builtinImpls(): ImplDecl[] {
  return stdlibDecls().impls.filter((im) => !(isGenericContainer(im.typeName) && im.trait === ""));
}

/** Every container `impl` block from `collections.stm` (inherent + `Iterable`).
 *  Their method `nameSpan`s serve as go-to-definition targets (via
 *  `containerMethodNameSpan`), giving `set.add` the same jump-to-source a `str`
 *  method has. */
export function builtinContainerImpls(): ImplDecl[] {
  return stdlibDecls().impls.filter((im) => isGenericContainer(im.typeName));
}

/** The *inherent* container `impl` blocks (`impl<K, V> Dict<K, V> { … }`) — the
 *  flat methods (`add`/`get`/`size`/…). The checker processes these like any user
 *  impl (they're kept out of the shared `builtinImpls` so the backends don't try to
 *  lower them as static calls) so a container's methods resolve through the same
 *  impl + substitution path a generic struct's do. */
export function builtinContainerInherentImpls(): ImplDecl[] {
  return builtinContainerImpls().filter((im) => im.trait === "");
}

/** The declared type-parameter names of a built-in container (`Dict` → `["K","V"]`),
 *  read off its `builtin struct` declaration in the stdlib — the container analogue
 *  of a generic struct's `typeParams`, and the same source that backs go-to-def on
 *  the type name. The checker and monomorphizer resolve a container's arguments the
 *  same way they resolve a struct's, so a container needs no bespoke arity model. */
export function containerTypeParams(name: string): string[] {
  return builtinStructs().find((s) => s.builtin && s.name === name)?.typeParams.map((p) => p.name) ?? [];
}

/** The declaration site of a container method (e.g. `Set`/`add`) in `collections.stm`,
 *  or `null` if the container has no such `builtin fn`. Lets a call to a container
 *  method resolve go-to-definition to the same source the method is documented in.
 *  Built once from `builtinContainerImpls`, keyed `"<Container>.<method>"`. */
export function containerMethodNameSpan(container: string, method: string): Span | null {
  return containerMethodSpans().get(`${container}.${method}`) ?? null;
}

let containerSpanCache: Map<string, Span> | null = null;
function containerMethodSpans(): Map<string, Span> {
  if (containerSpanCache) return containerSpanCache;
  const map = new Map<string, Span>();
  for (const im of builtinContainerImpls())
    for (const m of im.methods) map.set(`${im.typeName}.${m.name}`, m.nameSpan);
  return (containerSpanCache = map);
}

/** The built-in `fn` declarations (`builtin fn ...`) — those implemented
 *  *natively*. Each names a function the backends plug in by name; this is the
 *  checker's source of truth (via `BUILTINS`) for what a call to one returns. A
 *  prelude free function written *in STM* (a plain `fn` with a body, e.g.
 *  `min`/`max`, `awaitAll`) is not one of these — see `preludeFns`. */
export function builtinFns(): FnDecl[] {
  return stdlibDecls().fns.filter((f) => f.builtin);
}

/** The prelude's free functions written *in STM* — ordinary `fn`s with a body
 *  (not `builtin`), e.g. `min`/`max` and `awaitAll` (built on the `await`
 *  primitive). Unlike the native `builtinFns`, these are real functions: every
 *  stage that assembles the prelude (checker, interpreter, monomorphize) prepends
 *  them to the program, so their bodies are checked and lowered like any user
 *  function (a program may shadow one by declaring the same name). */
export function preludeFns(): FnDecl[] {
  return stdlibDecls().fns.filter((f) => !f.builtin);
}

// ---------------------------------------------------------------------------
// Built-in functions, as consumed by the checker and the language service. The
// table is *derived* from the `builtin fn` declarations above, so the source is
// the single point of truth for a builtin's name, return type, and signature.
// ---------------------------------------------------------------------------

/** Map a declared type expression to the checker's `Type`. Built-in signatures
 *  mention the primitives and — for file IO — the prelude's `Res` enum and its
 *  `IOError`/`Unit` payload structs, so a named type that resolves to a prelude
 *  struct/enum is carried through faithfully (e.g. `Res<IOError, string>` becomes
 *  the same `enum-generic` the checker's `resolveType` would produce). Anything
 *  else — an untyped parameter's `any`, a stray name — stays `unknown`. */
function typeExprToType(te: TypeExpr | null): Type {
  if (!te) return VOID;
  if (te.kind !== "named") return UNKNOWN;
  switch (te.name) {
    case "void":
      return VOID;
    case "string":
    case "str":
      return STRING;
    case "float":
      return FLOAT;
    case "int":
      return INT;
    case "bool":
      return BOOL;
  }
  const args = te.args.map(typeExprToType);
  if (builtinEnums().some((e) => e.name === te.name))
    return args.length ? { k: "enum-generic", name: te.name, args } : { k: "enum", name: te.name };
  if (builtinStructs().some((s) => s.name === te.name))
    return args.length ? { k: "struct-generic", name: te.name, args } : { k: "struct", name: te.name };
  return UNKNOWN;
}

/** Render a declared type expression for a one-line signature. */
function typeExprToStr(te: TypeExpr): string {
  if (te.kind !== "named") return "fn";
  return te.args.length ? `${te.name}<${te.args.map(typeExprToStr).join(", ")}>` : te.name;
}

/** A builtin's one-line signature, e.g. `randInt(n: int) int`. An untyped
 *  parameter (the placeholder `any`) shows just its name (`print(value)`); the
 *  return type is omitted when it is `void`. */
function builtinSig(fn: FnDecl): string {
  const params = fn.params
    .map((p) => (p.type.kind === "named" && p.type.name === "any" ? p.name : `${p.name}: ${typeExprToStr(p.type)}`))
    .join(", ");
  const ret = fn.returnType ? ` ${typeExprToStr(fn.returnType)}` : "";
  return `${fn.name}(${params})${ret}`;
}

/** Built-in functions, with their return type, display signature, declared effects
 *  (the trailing `! io retry raises spawn` annotation), and the `nameSpan` of their
 *  declaration in the stdlib source — derived from the `builtin fn` declarations in
 *  `STDLIB_SRC`. The effects seed the checker's effect system (IO and `spawn` are
 *  forbidden inside a `txn`; `retry` requires one; an uncaught `raises` may not escape
 *  one); the `nameSpan` (carrying its stdlib file `source`) is the go-to-definition
 *  target the checker records for a call. */
export const BUILTINS: Record<
  string,
  { ret: Type; sig: string; io: boolean; retry: boolean; raises: boolean; spawn: boolean; nameSpan: Span }
> = Object.fromEntries(
  builtinFns().map((fn) => [
    fn.name,
    {
      ret: typeExprToType(fn.returnType),
      sig: builtinSig(fn),
      io: fn.effects?.includes("io") ?? false,
      retry: fn.effects?.includes("retry") ?? false,
      raises: fn.effects?.includes("raises") ?? false,
      spawn: fn.effects?.includes("spawn") ?? false,
      nameSpan: fn.nameSpan,
    },
  ])
);

